import { useEffect, useState } from 'react';

declare global {
  interface Window {
    ymaps3?: any;
    __ymapsRefCount?: number;
    __ymapsCleanupTimer?: NodeJS.Timeout;
  }
}

interface UseYandexMapsScriptReturn {
  isLoaded: boolean;
  isError: boolean;
  errorMessage: string;
}

export const useYandexMapsScript = (apiKey: string): UseYandexMapsScriptReturn => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;
    let checkInterval: NodeJS.Timeout | undefined;
    let timeoutId: NodeJS.Timeout | undefined;

    window.__ymapsRefCount = (window.__ymapsRefCount || 0) + 1;
    if (window.__ymapsCleanupTimer) {
      clearTimeout(window.__ymapsCleanupTimer);
      window.__ymapsCleanupTimer = undefined;
    }

    if (window.ymaps3) {
      if (isMounted) {
        setIsLoaded(true);
      }
    } else {
      const existingScript = document.getElementById('yandex-maps-script');
      if (existingScript) {
        checkInterval = setInterval(() => {
          if (window.ymaps3) {
            clearInterval(checkInterval!);
            if (isMounted) {
              setIsLoaded(true);
            }
          }
        }, 50);

        timeoutId = setTimeout(() => {
          clearInterval(checkInterval!);
          if (!window.ymaps3 && isMounted) {
            setIsError(true);
            setErrorMessage('Map loading timed out.');
          }
        }, 10000);
      } else {
        const script = document.createElement('script');
        script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=en_US`;
        script.async = true;
        script.id = 'yandex-maps-script';

        timeoutId = setTimeout(() => {
          if (!window.ymaps3 && isMounted) {
            setIsError(true);
            setErrorMessage('Map loading timed out. Please check your connection or VPN settings.');
          }
        }, 10000);

        script.onload = () => {
          if (timeoutId) clearTimeout(timeoutId);
          if (window?.ymaps3?.import?.registerCdn) {
            try {
              window.ymaps3.import.registerCdn(
                'https://cdn.jsdelivr.net/npm/{package}',
                ['@yandex/ymaps3-default-ui-theme@0.0.3'],
              );
            } catch (e) {
              console.error('Failed to register CDN:', e);
            }
          }
          if (isMounted) {
            setIsLoaded(true);
          }
        };

        script.onerror = () => {
          if (timeoutId) clearTimeout(timeoutId);
          if (isMounted) {
            setIsError(true);
            setErrorMessage('Failed to load Yandex Maps. This may be due to VPN or regional restrictions.');
          }
        };

        document.head.appendChild(script);
      }
    }

    return () => {
      isMounted = false;
      if (checkInterval) clearInterval(checkInterval);
      if (timeoutId) clearTimeout(timeoutId);

      window.__ymapsRefCount = Math.max(0, (window.__ymapsRefCount || 1) - 1);
      if (window.__ymapsRefCount === 0) {
        window.__ymapsCleanupTimer = setTimeout(() => {
          if (window.__ymapsRefCount === 0) {
            import('../lib/ymap').then(({ cleanupYandexMaps }) => {
              cleanupYandexMaps();
            });

            const scriptElement = document.getElementById('yandex-maps-script');
            if (scriptElement) {
              scriptElement.remove();
            }
            window.__ymapsCleanupTimer = undefined;
          }
        }, 500);
      }
    };
  }, [apiKey]);

  return { isLoaded, isError, errorMessage };
};
